/* eslint-disable @typescript-eslint/no-explicit-any */
import { yupResolver } from '@hookform/resolvers/yup';
import { Image } from 'antd';
import { UploadChangeParam } from 'antd/lib/upload';
import { Upload } from 'feather-icons-react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button, Field, Indicator, Input, LinkButton, Switch } from 'components/common';

import { usePublisher } from '../../modules/publisher/usePublisher';
import { JPEG_FILE_MIME_TYPE, PNG_FILE_MIME_TYPE } from '../../utils/constants';
import { UploadButton, UploadFile } from '../common/UploadButton';
import { PublisherMappingChips } from './PublisherMappingChips';
import { PublisherMappingSearch } from './PublisherMappingSearch';
import {
  PublisherFormInputs,
  CreatePublisherModalProps,
  MAX_LENGTH,
  schema,
} from './publisherSettingsTypes';

type SearchResult = {
  type: 'deal' | 'site' | 'app';
  id: string;
  displayName: string;
};

export const CreatePublisherModal = ({ onClose }: CreatePublisherModalProps) => {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
    setValue,
  } = useForm<PublisherFormInputs>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    values: {
      id: '',
      name: '',
      displayName: '',
      icon: '',
      isAdformPublisher: false,
    },
  });
  const { createPublisher } = usePublisher();
  const errorText = null;
  const [iconFile, setIconFile] = useState<any | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<SearchResult[]>([]);
  const isAdformPublisher = watch('isAdformPublisher');

  const handleLogoUpload = useCallback(async ({ file }: UploadChangeParam<UploadFile>) => {
    return setIconFile(file);
  }, []);

  const handleEntitySelect = useCallback((entity: SearchResult) => {
    setSelectedEntities((prev) => [...prev, entity]);
  }, []);

  const handleEntityRemove = useCallback((entity: SearchResult) => {
    setSelectedEntities((prev) =>
      prev.filter((e) => !(e.type === entity.type && e.id === entity.id)),
    );
  }, []);

  const handleFormSubmit = handleSubmit(({ id, displayName }) => {
    const dealIds = selectedEntities.filter((e) => e.type === 'deal').map((e) => e.id);
    const siteDomains = selectedEntities.filter((e) => e.type === 'site').map((e) => e.id);
    const appIds = selectedEntities.filter((e) => e.type === 'app').map((e) => e.id);

    createPublisher({
      id: isAdformPublisher ? null : id,
      name: displayName,
      displayName,
      icon: iconFile,
      dealIds,
      siteDomains,
      appIds,
    });
    handleCancel();
  });

  const handleCancel = () => {
    reset();
    setIconFile(null);
    setSelectedEntities([]);
    onClose();
  };

  return (
    <div className="w-128 bg-white rounded-xl shadow  flex-col justify-start items-start inline-flex">
      <div className="self-stretch px-6 justify-start items-start gap-6 inline-flex">
        <div className="grow shrink basis-0 py-5 flex-col justify-start items-start gap-2 inline-flex">
          <h1 className="self-stretch text-base-md font-medium leading-snug text-secondary-black">
            Create new publisher
          </h1>
        </div>
      </div>
      <div className="px-6 pt-3 pb-6 gap-6 stretch-to-form">
        <div className="self-stretch flex-col justify-start items-start gap-1.5 flex">
          <form
            onSubmit={handleFormSubmit}
            className="w-full gap-4 flex flex-col justify-start items-start"
          >
            <div className="self-stretch justify-start items-center gap-4 inline-flex">
              {watch('icon') || iconFile ? (
                <div className="w-20 h-20 bg-primary-gray-50 rounded-lg border border-primary-gray-100 justify-center items-center flex">
                  {iconFile ? (
                    <Image
                      src={iconFile ? URL.createObjectURL(iconFile as Blob) : ''}
                      width={80}
                      height={80}
                      className="object-contain"
                      preview={false}
                    />
                  ) : (
                    <Image
                      src={watch('icon') || ''}
                      width={80}
                      height={80}
                      className="object-contain"
                      preview={false}
                    />
                  )}
                </div>
              ) : (
                <UploadButton
                  onChange={handleLogoUpload}
                  accept={`${PNG_FILE_MIME_TYPE}, ${JPEG_FILE_MIME_TYPE}`}
                >
                  <div className="w-20 h-20 bg-primary-gray-50 rounded-lg border border-primary-gray-100 justify-center items-center flex cursor-pointer">
                    <Upload className="w-6 h-6 relative" />
                  </div>
                </UploadButton>
              )}
              <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                <UploadButton
                  onChange={handleLogoUpload}
                  accept={`${PNG_FILE_MIME_TYPE}, ${JPEG_FILE_MIME_TYPE}`}
                >
                  <LinkButton className="h-auto py-0">Upload logo</LinkButton>
                </UploadButton>
                <div className="self-stretch text-primary-gray-500 text-base-xs leading-4">
                  Recommended specs: JPG format. Square 80px x 80px
                  <br />
                  minimum dimensions
                </div>
                {watch('icon') || iconFile ? (
                  <Button
                    variant="danger"
                    onClick={() => {
                      setIconFile(null);
                      setValue('icon', null, { shouldValidate: true, shouldDirty: true });
                    }}
                  >
                    Remove logo
                  </Button>
                ) : null}
              </div>
            </div>
            <Field
              label={'Dashboard Publisher name'}
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  {...field}
                  invalid={invalid}
                  placeholder="Enter publisher name..."
                  maxLength={MAX_LENGTH}
                />
              )}
              control={control}
              name={'displayName'}
              error={errors.displayName?.message}
              secondaryLabel={
                <div className="text-base-xs leading-tight font-medium flex items-center text-primary-gray-500">
                  {`${watch('displayName').length}/${MAX_LENGTH}`}
                </div>
              }
            />
            <div className="self-stretch flex-col justify-start items-start gap-2 flex">
              <div className="flex items-center justify-between w-full">
                <label className="text-secondary-black text-base-sm font-medium leading-snug">
                  Xandr Publisher ID
                </label>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isAdformPublisher || false}
                    onChange={(checked) => {
                      setValue('isAdformPublisher', checked, { shouldValidate: true });
                      if (checked) {
                        setValue('id', '', { shouldValidate: true });
                      }
                    }}
                  />
                  <span className="text-secondary-black text-base-sm">
                    This is Adform publisher
                  </span>
                </div>
              </div>
              <Field
                render={({ field, fieldState: { invalid } }) => (
                  <Input
                    {...field}
                    invalid={invalid}
                    placeholder={
                      isAdformPublisher ? 'Auto-assigned by system' : 'Enter Xandr Publisher ID...'
                    }
                    disabled={isAdformPublisher}
                  />
                )}
                control={control}
                name={'id'}
                error={!isAdformPublisher ? errors.id?.message : undefined}
              />
            </div>
            <div className="self-stretch flex-col justify-start items-start gap-4 flex">
              <label className="text-secondary-black text-base-sm font-medium leading-snug">
                Map to Deals, Sites, or Apps
              </label>
              <PublisherMappingSearch
                onSelect={handleEntitySelect}
                selectedEntities={selectedEntities}
              />
              <PublisherMappingChips entities={selectedEntities} onRemove={handleEntityRemove} />
            </div>
          </form>

          <div className="stretch-to-form gap-3">
            {errorText && (
              <Indicator variant="danger" size="lg">
                {errorText}
              </Indicator>
            )}
          </div>
        </div>
      </div>
      <div className="self-stretch h-16 px-6 border-t border-primary-gray-100 justify-end items-center gap-2 inline-flex">
        <div className="justify-end items-center gap-2 flex">
          <Button size="md" variant="secondary" onClick={handleCancel} className="w-20">
            Cancel
          </Button>
          <Button size="md" variant="primary" disabled={!isValid} onClick={handleFormSubmit}>
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
};
